import { briefsOf } from './briefRows';
import { type Recommendation, recommendationsOf } from './recommendationRows';
import type {
  EmptyState,
  NextAction,
  PresetOption,
  WorkflowRun,
  WorkflowStep,
} from './workflows.types';

// Synthetic service-shaped data, shared by the result and individual card stories.
const recommendationData = {
  id: 1,
  title: 'Open with an audience callout',
  attribute: 'Visual Elements',
  rationale: 'Show the product benefit before the lifestyle scene.',
  evidence: ['Split layouts performed better across 18 comparable creatives.'],
  tag_changes: [
    {
      action: 'Replace',
      feature: 'Hook Scene Layout Type',
      attribute: 'Visual Elements',
      current_tag: 'Hero Product',
      proposed_tag: 'Split',
    },
  ],
  projected_final_overall_score: 7.4,
  projected_final_delta: 0.8,
};
export const recommendation = recommendationsOf({
  recommendations: [recommendationData],
})[0] as Recommendation;
export const creative = {
  creative_id: 'demo-creative-1',
  name: 'Summer launch · Audience callout',
  type: 'VIDEO',
  network: 'meta',
  days_live: 13,
  metrics: [
    { metric: 'Spend', value: 2346.39, format: 'compact_currency' },
    { metric: 'CTR', value: 0.032, format: 'percentage' },
  ],
  status: 'Completed',
  error: null,
  final_overall_score: 6.6,
  best_practice_overall_score: 7.1,
  performance_overall_score: 6.2,
  brand_guidelines_overall_score: 8.4,
  recommendations: [
    recommendationData,
    {
      ...recommendationData,
      id: 2,
      title: 'Make the end-card offer explicit',
      tag_changes: [
        {
          action: 'Replace',
          feature: 'CTA',
          attribute: 'Text Elements',
          current_tag: 'Learn more',
          proposed_tag: 'Start your free trial',
        },
      ],
    },
  ],
};
const iterationResult = { result_type: 'iterations', creatives: [creative] };
export const briefResult = {
  creatives: [
    {
      ...creative,
      body: 'Keep the product recognizable. Make the offer readable without audio.',
      campaign_objectives: ['Conversions'],
      concepts: ['Product demonstration'],
      kpi: 'CTR',
      formats: ['9:16'],
      durations: ['15 seconds'],
    },
  ],
};
export const brief = briefsOf(briefResult)[0] as NonNullable<ReturnType<typeof briefsOf>[number]>;
export const gemResult = {
  gems: [
    {
      feature: 'Hook Scene Layout Type',
      tag: 'Split',
      category: 'Visual Elements',
      scene: 'Hook',
      network: 'meta',
      potential: 'High',
      spend_share: 0.08,
      creatives: ['demo-creative-1', 'demo-creative-2'],
      category_gem_count: 1,
      category_best_uplift: 0.34,
      metrics: [
        { metric: 'CTR', value: 0.041, format: 'percentage' },
        { metric: 'Spend', value: 1800, format: 'compact_currency' },
        { metric: 'vs. Account Avg', value: 0.34, format: 'percentage' },
      ],
    },
  ],
};
const spendOf = (value: number) => ({ metric: 'Spend', value, format: 'compact_currency' });
const ctrOf = (value: number) => ({ metric: 'CTR', value, format: 'percentage' });
const tagOf = (feature: string, scene: string, tag: string, ctr: number) => ({
  feature,
  scene,
  tag,
  category: 'Visual Elements',
  network: 'meta',
  carriers: 3,
  spend_share: 0.2,
  metrics: [spendOf(1200), ctrOf(ctr)],
});
const viewOf = (key: string, label: string, ctr: number, ids: string[]) => ({
  key,
  label,
  is_rest: key === 'b',
  spend: spendOf(key === 'a' ? 4200 : 9100),
  kpi: {
    ...ctrOf(ctr),
    previous_value: 0.03,
    change_percent: (ctr / 0.03 - 1) * 100,
    is_improvement: ctr > 0.03,
  },
  creative_count: key === 'a' ? 2 : 12,
  flags: key === 'a' ? ['low_sample'] : [],
  top_spender_ids: ids,
  top_performer_ids: ids.slice(0, 1),
  fatigued: [],
  elements: [
    tagOf('Layout Type', 'Hook Scene', key === 'a' ? 'Split' : 'Hero Product', ctr),
    tagOf('Voiceover', 'Core Scene', 'Female', ctr * 0.9),
  ],
});
export const comparisonLabels = {
  overview: 'Overview',
  spend: 'Spend',
  kpi: 'Average KPI',
  creative_count: 'Creatives',
  vs_dataset: 'vs. dataset average',
  top_spenders: 'Top spenders',
  top_performers: 'Top performers',
  fatigued: 'Fatigued creatives',
  feature_comparison: 'Feature comparison',
  hook_scenes: 'Hook scenes',
  rest_of_account: 'Rest of account',
  no_recommended_feature: 'Pick a feature to compare its tags.',
  'flag.low_sample': 'Few creatives or little spend; read with care.',
  'flag.empty_after_threshold': 'Nothing in this view clears the spend threshold.',
  'flag.no_data': 'No delivery in this view for the period.',
};
export const comparisonResult = {
  result_type: 'comparison',
  summary: '',
  kpi: 'ctr',
  breakdown_label: 'Country',
  views: [
    viewOf('a', 'US', 0.041, ['demo-creative-1']),
    viewOf('b', 'Rest of account', 0.027, ['demo-creative-2', 'demo-creative-1']),
  ],
  recommended_feature: { feature: 'Layout Type', scene: 'Hook Scene' },
  creatives: [
    {
      creative_id: 'demo-creative-1',
      type: 'VIDEO',
      network: 'meta',
      thumbnail_url: null,
      preview_url: null,
      metrics: [spendOf(2346.39), ctrOf(0.032)],
    },
    {
      creative_id: 'demo-creative-2',
      type: 'IMAGE',
      network: 'meta',
      thumbnail_url: null,
      preview_url: null,
      metrics: [spendOf(980), ctrOf(0.021)],
    },
  ],
};
export const scoresResult = {
  result_type: 'creative_scores',
  summary: 'Scored 6 creatives on CTR; 2 below the spend gate, 1 warming up',
  view_mode: 'top_worst',
  account_score: 58.4,
  account_score_count: 8,
  scored_count: 6,
  excluded_below_gate_count: 2,
  warming_up_count: 1,
  creatives: [
    scoredCreative('demo-creative-1', 86, 4200),
    scoredCreative('demo-creative-2', 64, 2500),
    scoredCreative('demo-creative-3', 41, 900),
    scoredCreative('demo-creative-4', 12, 300),
  ],
  score_points: [
    { creative_id: 'demo-creative-1', creative_type: 'VIDEO', score: 86, spend: 4200 },
    { creative_id: 'demo-creative-2', creative_type: 'VIDEO', score: 64, spend: 2500 },
    { creative_id: 'demo-creative-3', creative_type: 'VIDEO', score: 41, spend: 900 },
    { creative_id: 'demo-creative-4', creative_type: 'VIDEO', score: 12, spend: 300 },
    { creative_id: 'demo-creative-6', creative_type: 'IMAGE', score: 95, spend: 1200 },
    { creative_id: 'demo-creative-7', creative_type: 'IMAGE', score: 33, spend: 700 },
  ],
};
const comboCreative = (creativeId: string, ctr: number) => ({
  creative_id: creativeId,
  type: 'VIDEO',
  network: 'meta',
  days_live: 21,
  metrics: [
    { metric: 'Spend', value: 900, format: 'compact_currency' },
    { metric: 'CTR', value: ctr, format: 'percentage' },
  ],
});
export const combinationResult = {
  result_type: 'combinations',
  summary: '2 combinations beat the account average.',
  count_label: '2 combinations found',
  combinations: [
    {
      combo_id: 'meta|Hook Scene Layout Type=Split|Voiceover=Female',
      network: 'meta',
      elements: [
        { category: 'Structure', feature: 'Layout Type', scene: 'Hook Scene', tag: 'Split' },
        { category: 'Story', feature: 'Voiceover', scene: 'Core Scene', tag: 'Female' },
      ],
      size: 2,
      creatives: [comboCreative('demo-creative-1', 0.05), comboCreative('demo-creative-2', 0.04)],
      spend_share: 0.12,
      metrics: [
        { metric: 'Spend', value: 1800, format: 'compact_currency' },
        { metric: 'CTR', value: 0.045, format: 'percentage' },
        { metric: 'vs. Account Avg', value: 0.42, format: 'percentage', precision: 0 },
      ],
      checks: [
        {
          metric: 'First half',
          value: 0.4,
          format: 'percentage',
          precision: 0,
          is_improvement: true,
        },
        {
          metric: 'Second half',
          value: 0.44,
          format: 'percentage',
          precision: 0,
          is_improvement: true,
        },
        {
          metric: 'Without best creative',
          value: 0.35,
          format: 'percentage',
          precision: 0,
          is_improvement: true,
        },
      ],
      verdict: 'Holds up',
      variants_label: 'Similar combinations',
      correlated_note:
        "Always appears with another element in this set, so the lift can't be credited to either one alone.",
      correlated: [[0, 1]],
      variants: [
        {
          elements: [
            { category: 'Structure', feature: 'Layout Type', scene: 'Hook Scene', tag: 'Split' },
          ],
          size: 1,
          support: 5,
          uplift: 0.3,
        },
      ],
      variant_count: 3,
    },
    {
      combo_id: 'meta|Background=Blurry|Voiceover=Male',
      network: 'meta',
      elements: [
        { category: 'Graphics', feature: 'Background', scene: 'Core Scene', tag: 'Blurry' },
        { category: 'Story', feature: 'Voiceover', scene: 'Core Scene', tag: 'Male' },
      ],
      size: 2,
      creatives: [comboCreative('demo-creative-3', 0.06)],
      spend_share: 0.04,
      metrics: [
        { metric: 'Spend', value: 600, format: 'compact_currency' },
        { metric: 'CTR', value: 0.06, format: 'percentage' },
        { metric: 'vs. Account Avg', value: 0.25, format: 'percentage', precision: 0 },
      ],
      checks: [],
      verdict: 'Holds up',
      correlated: [],
      variants: [],
      variant_count: 0,
    },
  ],
};
// Chip text as tRPC formats the service's preset thresholds.
export const fatiguePresets: PresetOption[] = [
  {
    id: 'moderate',
    label: 'Moderate',
    description: 'Fewer, high-confidence flags',
    icon: 'clipboard',
    tag: null,
    tooltip: null,
    chips: [
      { label: 'CTR', text: '≥ 20% drop' },
      { label: 'CPM', text: '≥ 10% rise' },
    ],
  },
  {
    id: 'balanced',
    label: 'Balanced',
    description: 'Catch early, no over-flagging',
    icon: 'flame',
    tag: 'RECOMMENDED',
    tooltip: null,
    chips: [
      { label: 'CTR', text: '≥ 15% drop' },
      { label: 'CPM', text: '≥ 10% rise' },
    ],
  },
  {
    id: 'aggressive',
    label: 'Aggressive',
    description: 'Catch it early',
    icon: 'bolt',
    tag: null,
    tooltip: null,
    chips: [
      { label: 'CTR', text: '≥ 10% drop' },
      { label: 'CPM', text: 'any increase' },
    ],
  },
];
export const fatiguedCreative = {
  creative_id: 'demo-fatigued-1',
  name: 'Spring promo · Hero product',
  type: 'VIDEO',
  network: 'meta',
  days_live: 19,
  metrics: [
    { metric: 'Spend', value: 1033.78, format: 'compact_currency' },
    { metric: 'CTR', value: 0.0149, format: 'percentage' },
  ],
  fatigue: {
    preset: 'balanced',
    ctr_ratio: 0.72,
    cpm_ratio: 1.24,
    recent_spend: 746.44,
    active_days: 14,
    impressions_recent: 75630,
  },
};
// As the actions service declares it for getFatigue, mapped by tRPC.
export const fatigueEmptyState: EmptyState = {
  badge: 'ALL CLEAR',
  title: "Nothing's fatigued right now",
  body: "None of the live creatives in scope crossed {standard}, so there's nothing to iterate on today.",
  presetStandard: 'your {preset} preset',
  customStandard: 'your custom thresholds',
  defaultStandard: 'your detection preset',
  chipsLabel: 'Detection thresholds',
  editCta: 'Edit preset',
};
export const fatigueConfigStep: WorkflowStep = {
  stepId: 'getFatigue-config',
  index: 1,
  kind: 'config',
  title: 'Choose a detection preset',
  layout: 'stack',
  fields: [
    {
      kind: 'presetCards',
      key: 'preset',
      label: 'Detection preset',
      hint: null,
      tooltip: null,
      required: true,
      default: 'balanced',
      options: fatiguePresets,
    },
    {
      kind: 'collapsible',
      key: 'advanced_overrides',
      label: 'Custom thresholds',
      hint: null,
      tooltip: null,
      required: false,
      fields: [],
    },
  ],
  description: null,
  tooltip: null,
  summaryLabel: 'Detection preset',
  settingsTitle: null,
  submit: {
    actionId: 'getFatigue',
    label: 'Scan for fatigue',
    runningTitle: 'Scanning for fatigue…',
    runningSteps: ['Pulling live creatives in scope…'],
  },
  decisions: [],
  guardrails: null,
};
export const run: WorkflowRun = {
  runId: 'demo-run',
  actionType: 'generateIterations',
  status: 'completed',
  completedAt: null,
  result: iterationResult,
  resultSchemaVersion: 8,
  resultKind: 'creative_list',
  actionName: 'Iteration Recommendations',
  outcomeTitle: 'Iteration recommendations',
  outcomeKind: 'brief',
  nextActions: [],
  itemCount: 1,
  errorMessage: null,
};
function scoredCreative(id: string, score: number | null, spend: number) {
  const component = (key: string, label: string, value: number | null) => ({
    key,
    label,
    score: value,
    weight: 0.2,
    insufficient_sample: false,
  });
  return {
    creative_id: id,
    type: 'VIDEO',
    network: 'meta',
    days_live: 21,
    score,
    spend,
    metrics: [
      { metric: 'Score', value: score, format: 'number', precision: 0 },
      { metric: 'Spend', value: spend, format: 'compact_currency', currency_symbol: '$' },
    ],
    components:
      score === null
        ? []
        : [
            component('hook', 'Hook', Math.min(100, score + 8)),
            component('engagement', 'Engagement', score),
            component('watch', 'Watch', Math.max(1, score - 12)),
            component('leadingKpi', 'CTR', score),
            component('elements', 'Elements', 50),
          ],
  };
}
// The service echoes the run's params onto its result, `preset` among them.
export function fatigueRun(
  creatives: unknown[],
  params: Record<string, unknown> = { preset: 'balanced' },
): WorkflowRun {
  return {
    ...run,
    runId: 'demo-fatigue-run',
    actionType: 'getFatigue',
    result: {
      ...params,
      result_type: 'fatigued_creatives',
      summary: `Found ${creatives.length} fatigued creatives`,
      creatives,
    },
    resultKind: 'creative_list',
    actionName: 'Creative Fatigue',
    outcomeTitle: 'Fatigue report',
    outcomeKind: 'report',
    itemCount: creatives.length,
  };
}
export function nextAction(
  overrides: Partial<NextAction> & Pick<NextAction, 'actionType' | 'label'>,
): NextAction {
  return {
    description: '',
    cta: '',
    ctaOne: '',
    enabled: true,
    reason: null,
    recommended: false,
    requiresSelection: true,
    minSelection: 1,
    maxSelection: null,
    ...overrides,
  };
}
